import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { X, Sparkles, Share2, GraduationCap } from "lucide-react";

interface OnboardingNotification {
  id: string;
  notification_key: string;
  is_read: boolean;
  shown_at: string | null;
}

const NOTIFICATION_CONFIG: Record<string, {
  message: string;
  icon: typeof Sparkles;
  cta: string;
  action: 'image' | 'tooltip' | 'tutorials';
  delayAfterPrevious: number; // seconds
}> = {
  free_images: {
    message: "Generate images for free!",
    icon: Sparkles,
    cta: "Try Now",
    action: 'image',
    delayAfterPrevious: 0,
  },
  share_public: {
    message: "Share creations to get noticed",
    icon: Share2,
    cta: "Got it",
    action: 'tooltip',
    delayAfterPrevious: 10,
  },
  tutorials: {
    message: "Master AI creation with tutorials",
    icon: GraduationCap,
    cta: "View",
    action: 'tutorials',
    delayAfterPrevious: 10,
  },
};

const NOTIFICATION_ORDER = ['free_images', 'share_public', 'tutorials'];

export function OnboardingNotification() {
  const [notifications, setNotifications] = useState<OnboardingNotification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<OnboardingNotification | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (notifications.length === 0) return;

    // Find the first unread notification in order
    const nextUnread = NOTIFICATION_ORDER
      .map(key => notifications.find(n => n.notification_key === key && !n.is_read))
      .find(n => n !== undefined);

    if (nextUnread) {
      const config = NOTIFICATION_CONFIG[nextUnread.notification_key];
      const previousKey = NOTIFICATION_ORDER[NOTIFICATION_ORDER.indexOf(nextUnread.notification_key) - 1];
      const previousNotification = previousKey ? notifications.find(n => n.notification_key === previousKey) : null;
      
      // Check if previous was read recently (within last minute) - if so, add delay
      const shouldDelay = previousNotification?.is_read && previousNotification?.shown_at;
      const delay = shouldDelay ? config.delayAfterPrevious * 1000 : 0;

      const timer = setTimeout(() => {
        setCurrentNotification(nextUnread);
        setIsVisible(true);
        markAsShown(nextUnread.id);
      }, delay);

      return () => clearTimeout(timer);
    } else {
      setCurrentNotification(null);
      setIsVisible(false);
    }
  }, [notifications]);

  const fetchNotifications = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('onboarding_notifications')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching onboarding notifications:', error);
      return;
    }

    setNotifications(data || []);
  };

  const markAsShown = async (id: string) => {
    await supabase
      .from('onboarding_notifications')
      .update({ shown_at: new Date().toISOString() })
      .eq('id', id);
  };

  const handleDismiss = async () => {
    if (!currentNotification) return;

    // Mark as read
    const { error } = await supabase
      .from('onboarding_notifications')
      .update({ is_read: true })
      .eq('id', currentNotification.id);

    if (error) {
      console.error('Error updating notification:', error);
      return;
    }

    // Update local state
    setNotifications(prev => 
      prev.map(n => n.id === currentNotification.id ? { ...n, is_read: true } : n)
    );
    setIsVisible(false);
  };

  const handleAction = async () => {
    if (!currentNotification) return;

    const config = NOTIFICATION_CONFIG[currentNotification.notification_key];
    
    // Mark as read first
    await handleDismiss();

    // Perform action
    if (config.action === 'image') {
      // Dispatch event to switch to image tab
      window.dispatchEvent(new CustomEvent('use-prompt', { 
        detail: { prompt: '', tab: 'image' } 
      }));
    } else if (config.action === 'tutorials') {
      navigate('/tutorials');
    }
    // 'tooltip' action just dismisses
  };

  if (!isVisible || !currentNotification) return null;

  const config = NOTIFICATION_CONFIG[currentNotification.notification_key];
  const Icon = config.icon;

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-4 fade-in duration-300">
      <div className="bg-primary text-primary-foreground px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-sm">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-sm font-medium flex-1">{config.message}</p>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleAction}
          className="flex-shrink-0 h-7 px-3 text-xs"
        >
          {config.cta}
        </Button>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 hover:bg-primary-foreground/20 rounded transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
